import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildLangUrl } from '../lib/langUrl';

// All language definitions are kept here so we don't lose the translations.
// `enabled` controls whether each language is rendered in the public selector.
// Roadmap: this list will be replaced by a runtime fetch of /api/config/public
// (collection app_config in Mongo) so the superadmin can toggle languages from
// the AdminPanel without redeploying.
const LANGUAGES = [
  { code: 'en', label: 'EN', enabled: true  },
  { code: 'es', label: 'ES', enabled: true  },
  // Sprint 119.17: FR i IT reactivats. La reorientació a certificació
  // ambiental EN 17645 (norma europea) legitima obrir la web als
  // mercats francès i italià — el marc regulador és el mateix a tota
  // la UE. Traduccions al bundle des del sprint inicial i actualitzades
  // amb cada canvi de copy.
  { code: 'fr', label: 'FR', enabled: true  },
  // Sprint 107j: DE reactivat. GSC (juliol 2026) mostra 3 queries alemanyes
  // amb impressions per calculadora de clor (schockchlorung / chlor im pool
  // rechner). Amb de.json ja al bundle, no cal cap altra feina — només el
  // selector i el langMap de SEO.js.
  { code: 'de', label: 'DE', enabled: true  },
  { code: 'it', label: 'IT', enabled: true  },
  // Sprint 119.97: NL activat. GSC (2026-08-12) mostra Netherlands amb
  // 21 impressions/setmana i posició mitjana 5.86 — la millor posició de
  // tots els països (excepte Corea amb 1 impressió). Mercat wellness
  // maduro amb piscines residencials elevades i EN 17645:2023 aplica
  // igualment (marc UE comú). Norma NL: NEN-EN 17645.
  { code: 'nl', label: 'NL', enabled: true  },
];

// `enabled` no canvia en temps d'execució, així que es calcula un sol cop.
const VISIBLE = LANGUAGES.filter(l => l.enabled);

const LanguageSelector = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = VISIBLE.find(l => i18n.language?.startsWith(l.code))?.code || VISIBLE[0]?.code || 'en';

  // En quins idiomes existeix AQUESTA ruta. Els altres es pinten igual
  // però apagats: la tira es manté sencera a tot el lloc i es veu d'un
  // cop quines llengües té la pàgina. L'actual sempre compta com a
  // disponible, encara que no n'hi hagi cap altra.
  const disponibles = useMemo(
    () => new Set(
      VISIBLE
        .filter(l => l.code === currentLang
          || buildLangUrl(location.pathname, l.code) !== null)
        .map(l => l.code),
    ),
    [location.pathname, currentLang],
  );

  const handleLangChange = (newLang) => {
    // NO es toca i18n.changeLanguage: l'idioma el decideix la URL i el
    // LanguageSync l'hi alinea. Canviar-lo aquí a mà era precisament el
    // que no s'aguantava. Si no hi ha destí, el botó està deshabilitat.
    const targetUrl = buildLangUrl(location.pathname, newLang);
    if (targetUrl && targetUrl !== location.pathname) {
      navigate(targetUrl);
    }
  };

  return (
    <div className="flex items-center gap-0.5 text-xs font-mono" data-testid="language-selector">
      {VISIBLE.map((lang, i) => {
        const hiHaVersio = disponibles.has(lang.code);
        return (
          <React.Fragment key={lang.code}>
            <button
              onClick={() => handleLangChange(lang.code)}
              disabled={!hiHaVersio}
              aria-disabled={!hiHaVersio}
              className={`px-1.5 py-0.5 transition-colors ${
                !hiHaVersio
                  ? 'text-slate-200 cursor-default'
                  : currentLang === lang.code
                    ? 'text-slate-900 font-bold'
                    : 'text-slate-400 hover:text-slate-600'
              }`}
              data-testid={`lang-${lang.code}`}
            >
              {lang.label}
            </button>
            {i < VISIBLE.length - 1 && <span className="text-slate-300">|</span>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default LanguageSelector;
